import { useCallback, useEffect, useRef, useState } from "react";

import { clockNow, useFrameClock } from "./useFrameClock";
import {
  clampView,
  INITIAL_VIEW,
  refit,
  setView,
  viewAt,
  VIEWPORT,
  type Bounds,
  type View,
  type ViewState,
} from "./viewport";

export interface BoardViewport {
  /** The player's view at this frame (identity when fitted). */
  view: View;
  /** The view right now, for a gesture to start from. */
  getView: () => View;
  /** Set the view from the current one; clamped to keep content on screen. */
  update: (fn: (v: View) => View) => void;
  /** Back to the auto-fit (gliding unless reduced motion). */
  refitNow: () => void;
}

/**
 * The player's zoom/pan view. It persists until `moveKey` changes (a move or
 * a new game), then glides back to the fit; reduced motion snaps.
 */
export function useBoardViewport(
  size: { w: number; h: number } | null,
  bounds: Bounds | null,
  moveKey: string,
  reduced: boolean,
): BoardViewport {
  const [state, setState] = useState<ViewState>(INITIAL_VIEW);
  const ctx = useRef({ size, bounds, reduced, state });
  ctx.current = { size, bounds, reduced, state };

  const lastKey = useRef(moveKey);
  useEffect(() => {
    if (moveKey === lastKey.current) return;
    lastKey.current = moveKey;
    setState((s) => refit(s, clockNow(), reduced));
  }, [moveKey, reduced]);

  // A resized board re-fits at once.
  const w = size?.w ?? 0;
  const h = size?.h ?? 0;
  useEffect(() => setState(INITIAL_VIEW), [w, h]);

  const until = state.glide ? state.glide.t0 + VIEWPORT.glideMs : 0;
  const now = useFrameClock(until, reduced);
  const view = viewAt(state, now);

  const getView = useCallback(() => viewAt(ctx.current.state, clockNow()), []);
  const update = useCallback((fn: (v: View) => View) => {
    setState((s) => {
      const { size: sz, bounds: b } = ctx.current;
      const next = fn(viewAt(s, clockNow()));
      return setView(sz && b ? clampView(next, b, sz) : next);
    });
  }, []);
  const refitNow = useCallback(
    () => setState((s) => refit(s, clockNow(), ctx.current.reduced)),
    [],
  );

  return { view, getView, update, refitNow };
}
